import { inject, Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  addDoc,
  serverTimestamp,
} from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { Auth, authState, User } from '@angular/fire/auth';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrderAttribution } from '../classes/PaymentPlans';
import { ConsentService } from './consent.service';
import { MetaPixelEvent, MetaPixelService } from './meta-pixel.service';

export interface CampaignData {
  utm_id?: string | null;
  utm_source: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content: string | null;
  utm_term?: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
}

/** Erste und aktuelle Zuordnung - Basis fuer Order-Anreicherung und Reporting. */
export interface AttributionSnapshot {
  first: CampaignData | null;
  current: CampaignData | null;
}

/**
 * Ereignisnamen = Firestore-Pfad unter analytics/{name}/items.
 * Reihenfolge entspricht dem Funnel, damit das Dashboard sie so darstellen kann.
 */
export type AnalyticsEventName =
  | 'form-started'
  | 'song-create'
  | 'song-played'
  | 'plans-viewed'
  | 'checkout-started'
  | 'purchase'
  | 'signup';

/**
 * Interner Platzierungs-Hinweis: welcher Abschnitt der Website in die App
 * geführt hat (hero, pricing, footer …).
 *
 * Bewusst KEIN utm_-Parameter und bewusst nicht in CAMPAIGN_PARAMS. Das ist
 * eine Navigationsinformation, keine Akquisitionsquelle. Stünde dort
 * `utm_source=website`, würde jeder organische Besucher als Kampagne gezählt —
 * und schlimmer: ein vorhandenes `utm_source=meta` würde überschrieben und die
 * Anzeigen-Zuordnung ginge verloren.
 */
const PLACEMENT_PARAM = 'mw_placement';
const PLACEMENT_STORAGE_KEY = 'melowish_placement';

/**
 * Aufgeloeste Herkunft der laufenden Sitzung.
 *
 * Bewusst `sessionStorage` und bewusst NICHT einwilligungspflichtig: der Wert
 * lebt nur so lange wie der Tab und dient dazu, die Ereignisse **einer**
 * Sitzung untereinander zuzuordnen. Damit funktioniert er fuer 100 % der
 * Besucher - anders als `firstCampaign`, dessen sitzungsuebergreifende
 * Persistenz seit 1.2 an der Zustimmung haengt.
 *
 * Der Grund fuer das Feld: `track()` schrieb bisher nur `firstCampaign`. Ohne
 * UTM war damit fuer jede Stufe nach dem Aufruf keine Quelle mehr bestimmbar,
 * und das Dashboard musste auf "direkt" zurueckfallen - eine ueber den
 * Referrer gekommene Quelle sah dadurch zwangslaeufig wie null Conversions aus.
 */
const SESSION_SOURCE_STORAGE_KEY = 'melowish_session_source';

/** Der echte Referrer, vom Legacy-Shim in index.html gerettet. Siehe unten. */
const ORIGINAL_REFERRER_STORAGE_KEY = 'melowish_original_referrer';

/** Fallback-Quelle, wenn weder Kampagne noch externer Referrer vorliegen. */
const DIRECT_SOURCE = 'direkt';

/**
 * Hosts, die eine **Rueckkehr** in einen eigenen Ablauf bedeuten, keinen neuen
 * Besucher: der Firebase-Auth-Handler nach einem OAuth-Redirect und Stripe nach
 * dem Checkout. Der eigene Host kommt in `isInternalReferrer` dynamisch dazu,
 * damit dev, prod und localhost ohne Pflegeliste abgedeckt sind.
 *
 * `melowish.com` steht hier ausdruecklich NICHT drin - die Marketing-Seite ist
 * eine echte Quelle, der Sprung von dort ist ein echter Wechsel.
 */
const INTERNAL_RETURN_HOSTS = [
  '.firebaseapp.com',
  '.web.app',
  'checkout.stripe.com',
] as const;

/** Alle Parameter, die als Kampagnen-Herkunft gelten. */
const CAMPAIGN_PARAMS = [
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
] as const;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private fireDb = inject(Firestore);
  private authService = inject(AuthService);
  private fireAuth = inject(Auth);
  private consentService = inject(ConsentService);
  private metaPixelService = inject(MetaPixelService);
  private readonly firstAttributionKey = 'melowish_first_attribution';
  private readonly currentAttributionKey = 'melowish_current_attribution';
  private readonly sessionKey = 'melowish_session_id';

  /**
   * Der Referrer dieses Dokuments - einmal in `init()` bestimmt.
   *
   * Nicht direkt `document.referrer` lesen: hat der Legacy-Shim in index.html
   * per `location.replace()` umgeschrieben, steht dort die **eigene** URL, und
   * der echte Verweis waere verloren. Der Shim legt ihn deshalb vorher weg.
   */
  private originalReferrer: string | null = null;

  constructor() {}

  // =====================================================
  // INIT
  // =====================================================

  // Seit der Umstellung auf Path-Routing ist die kanonische Form:
  //   https://app.melowish.com/song-create?utm_source=tiktok
  //
  // Die alten Hash-Formen werden weiterhin unterstuetzt, weil verschickte Links
  // nie verschwinden. Der Shim in index.html leitet sie um, bevor Angular
  // startet - das Auslesen hier ist die zweite Sicherung, falls er nicht greift.
  async init(): Promise<void> {
    // Zuerst, weil sowohl die Quellenaufloesung als auch trackView() darauf
    // zugreifen und der geparkte Wert nur einmal gelesen werden darf.
    this.originalReferrer = this.takeOriginalReferrer();

    const currentCampaign = this.getCampaignFromUrl();
    const hasCurrentCampaign = this.hasCampaign(currentCampaign);

    if (hasCurrentCampaign) {
      // Aktuelle Zuordnung fuer diese Sitzung. capturedAt wird fuer den
      // fbc-Parameter gebraucht - dort zaehlt der Klickzeitpunkt, nicht der
      // Kaufzeitpunkt.
      this.writeJson(sessionStorage, this.currentAttributionKey, {
        ...currentCampaign,
        capturedAt: Date.now(),
      });

      // First-Touch ueberdauert die Sitzung und ist damit einwilligungspflichtig
      // (TDDDG §25). Der Schalter steuert, ob wir uns daran halten muessen:
      //
      //   consentRequired = false -> Testphase, wird unbedingt gespeichert
      //   consentRequired = true  -> nur mit erteilter Einwilligung
      //
      // MUSS vor dem ersten Ad-Budget auf true. Siehe environment.ts.
      const mayPersist =
        !environment.consentRequired || this.consentService.hasMarketingConsent();

      if (mayPersist && !this.readJson(localStorage, this.firstAttributionKey)) {
        this.writeJson(localStorage, this.firstAttributionKey, {
          ...currentCampaign,
          firstSeenAt: Date.now(),
        });
      }
    }

    this.capturePlacement();

    // Muss vor trackView() stehen: schon das Aufruf-Ereignis traegt die Quelle,
    // sonst faengt der Trichter mit einer anderen Zuordnung an als er endet.
    this.resolveSessionSource(hasCurrentCampaign ? currentCampaign : null);

    await this.trackView(currentCampaign);
  }

  /**
   * Platzierung merken, solange die Sitzung laeuft. Dadurch traegt auch das
   * Kauf-Ereignis noch die Information, welcher Abschnitt der Website den
   * Besucher gebracht hat.
   */
  private capturePlacement(): void {
    const params = new URLSearchParams(window.location.search);
    const placement = params.get(PLACEMENT_PARAM);
    if (placement) {
      try {
        sessionStorage.setItem(PLACEMENT_STORAGE_KEY, placement);
      } catch {
        // Storage blockiert: Platzierung geht verloren, Tracking laeuft weiter.
      }
    }
  }

  private getPlacement(): string | null {
    try {
      return sessionStorage.getItem(PLACEMENT_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Platzierung aus der App heraus setzen.
   *
   * `capturePlacement()` liest `mw_placement` aus der URL, und das passiert nur
   * beim Seitenaufbau. Ein CTA **innerhalb** der App wechselt die Route ohne
   * Neuladen - dort wuerde weder ein Query-Parameter noch ein UTM je gelesen.
   * Deshalb der direkte Weg.
   *
   * Bewusst `placement` und ausdruecklich kein `utm_*`: welchen Knopf jemand
   * auf unserer eigenen Seite gedrueckt hat, ist Navigation, keine
   * Akquisitionsquelle. Siehe die Regel im Handoff.
   */
  setPlacement(placement: string): void {
    if (!placement) return;
    try {
      sessionStorage.setItem(PLACEMENT_STORAGE_KEY, placement);
    } catch {
      // Storage blockiert: Ereignisse gehen ohne Platzierung raus.
    }
  }

  // =====================================================
  // SITZUNGSQUELLE
  // =====================================================

  /**
   * Legt die Herkunft der Sitzung fest: Kampagne, sonst externer Referrer,
   * sonst Direktaufruf.
   *
   * Einmal je Sitzung. Einzige Ausnahme beim Ueberschreiben: eine echte
   * Kampagnenquelle darf ein zuvor gespeichertes `direkt` ersetzen. Das ist
   * immer die bessere Information - der umgekehrte Fall waere ein Verlust und
   * ist deshalb ausgeschlossen.
   */
  private resolveSessionSource(currentCampaign: CampaignData | null): string {
    const stored = this.getSessionSource();
    const utm = currentCampaign?.utm_source || null;

    if (stored && !(utm && stored === DIRECT_SOURCE)) {
      return stored;
    }

    const source = utm || this.externalReferrerHost() || DIRECT_SOURCE;

    try {
      sessionStorage.setItem(SESSION_SOURCE_STORAGE_KEY, source);
    } catch {
      // Storage blockiert: das Ereignis geht ohne Quelle raus, nicht gar nicht.
    }

    return source;
  }

  private getSessionSource(): string | null {
    try {
      return sessionStorage.getItem(SESSION_SOURCE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** Referrer-Host, aber nur wenn er tatsaechlich von aussen kommt. */
  private externalReferrerHost(): string | null {
    const host = this.extractHost(this.originalReferrer);
    if (!host || this.isInternalReferrer(host)) return null;
    return host;
  }

  /**
   * Eigener Host oder Rueckkehr aus einem eigenen Ablauf.
   *
   * Der eigene Host kommt aus `window.location.hostname`, damit dev, prod und
   * localhost ohne gepflegte Liste abgedeckt sind - dieselbe Ueberlegung wie
   * bei der bewusst entfernten Suno-Host-Allowlist: eine Liste, die jemand
   * pflegen muss, faellt irgendwann still um.
   */
  private isInternalReferrer(host: string): boolean {
    if (host === window.location.hostname) return true;

    return INTERNAL_RETURN_HOSTS.some(
      (entry) => host === entry || host.endsWith(entry)
    );
  }

  /**
   * Den vom Shim geparkten Referrer holen und den Platz raeumen.
   *
   * Einmalig, weil der Wert genau zu dem einen Dokument gehoert, das der Shim
   * ersetzt hat. Bliebe er stehen, wuerde ein spaeterer Vollreload in derselben
   * Sitzung faelschlich denselben Referrer erben.
   */
  private takeOriginalReferrer(): string | null {
    try {
      const stashed = sessionStorage.getItem(ORIGINAL_REFERRER_STORAGE_KEY);
      if (stashed !== null) {
        sessionStorage.removeItem(ORIGINAL_REFERRER_STORAGE_KEY);
        return stashed || null;
      }
    } catch {
      // faellt unten auf document.referrer zurueck
    }

    return document.referrer || null;
  }

  /**
   * Wird nach erteilter Einwilligung aufgerufen: die bereits in dieser Sitzung
   * erfasste Zuordnung darf jetzt dauerhaft gespeichert werden.
   *
   * Aktuell ungenutzt - solange das Gate in init() aus ist, wird First-Touch
   * ohnehin direkt geschrieben. Wird mit 1.2 gebraucht.
   */
  persistFirstTouchAfterConsent(): void {
    if (!this.consentService.hasMarketingConsent()) return;
    if (this.readJson(localStorage, this.firstAttributionKey)) return;

    const current = this.readJson(sessionStorage, this.currentAttributionKey);
    if (!current) return;

    this.writeJson(localStorage, this.firstAttributionKey, {
      ...current,
      firstSeenAt: Date.now(),
    });
  }

  private async getInitialAuthUser(): Promise<User | null> {
    return firstValueFrom(
      authState(this.fireAuth).pipe(
        timeout(5000),
        catchError(() => of(null))
      )
    );
  }

  // =====================================================
  // VIEW TRACKING
  // =====================================================

  async trackView(currentCampaign?: CampaignData | null): Promise<void> {
    const hasCurrentCampaign = this.hasCampaign(currentCampaign || null);

    const collectionName = hasCurrentCampaign
      ? 'campaign-views'
      : 'organic-views';

    // Der Hash enthaelt den Querystring mit. Ohne Abschneiden wird jede
    // UTM-Kombination ein eigener "path": der Dedupe-Key unten greift nicht
    // mehr und die Seitengruppierung im Dashboard zerfaellt.
    const path = this.getCleanPath();

    // verhindert reload spam
    const dedupeKey = `tracked:${collectionName}:${path}`;

    if (this.hasTracked(dedupeKey)) {
      return;
    }
    this.markTracked(dedupeKey);

    const user = await this.getInitialAuthUser();

    await this.track(collectionName, {
      path,
      fullUrl: window.location.href,
      referrer: this.originalReferrer,
      referrerHost: this.extractHost(this.originalReferrer),
      campaign: hasCurrentCampaign ? currentCampaign : null,
      firstCampaign: this.getStoredCampaign(),
      userId: user?.uid || null,
    });
  }

  // =====================================================
  // BUSINESS EVENTS
  // =====================================================

  async trackSongCreate(data: any = {}): Promise<void> {
    await this.track('song-create', data);
  }

  async trackSignup(data: any = {}): Promise<void> {
    await this.track('signup', data);
  }

  /**
   * Interner Kauf-Datenpunkt. Bewusst nicht die Quelle fuer die Optimierung der
   * Werbeplattformen - das Purchase-Event dorthin feuert serverseitig aus dem
   * Stripe-Webhook, weil das unabhaengig von Einwilligung, Adblockern und
   * geschlossenen Tabs funktioniert.
   */
  async trackPurchase(data: any = {}): Promise<void> {
    await this.track('purchase', data);
  }

  // =====================================================
  // BASE TRACK
  // =====================================================

  private async track(
    collectionName: string,
    data: any = {}
  ): Promise<void> {
    // Meta zuerst, damit ein Firestore-Fehler das Werbe-Event nicht verhindert
    // - und umgekehrt: der Pixel wirft nicht, er verschluckt eigene Fehler.
    this.forwardToMetaPixel(collectionName, data);

    const ref = collection(
      this.fireDb,
      `analytics/${collectionName}/items`
    );

    await addDoc(ref, {
      sessionId: this.getSessionId(),
      createdAt: serverTimestamp(),

      firstCampaign: this.getStoredCampaign(),
      // Welcher Abschnitt der Website hierher gefuehrt hat. Getrennt von der
      // Kampagne, damit ein organischer Besucher nicht als Kampagne zaehlt.
      placement: this.getPlacement(),
      // Die aufgeloeste Herkunft der Sitzung - auf JEDEM Ereignis, nicht nur
      // auf dem Aufruf. Ohne das ist keine Quelle unterhalb der ersten
      // Trichterstufe bestimmbar.
      sessionSource: this.getSessionSource(),

      ...data,
    });
  }

  /**
   * Spiegelt Funnel-Ereignisse an den Meta-Browser-Pixel.
   *
   * Bewusst hier zentral: `track()` ist die einzige Stelle, durch die jedes
   * Ereignis laeuft. Der Pixel prueft selbst, ob eine Einwilligung vorliegt.
   *
   * `purchase` fehlt absichtlich - das Kauf-Ereignis geht ausschliesslich
   * serverseitig aus dem Stripe-Webhook. Zwei Quellen wuerden Doppelzaehlungen
   * erzeugen, und die serverseitige ist die verlaesslichere.
   *
   * Die Zuordnung folgt der Learning-Phase-Strategie: optimiert wird auf
   * AddToCart (= Song angefordert), weil dort genug Volumen entsteht;
   * Purchase ist bei diesem Umsatzniveau zu selten, um daraus zu lernen.
   */
  private forwardToMetaPixel(collectionName: string, data: any): void {
    const map: Record<string, MetaPixelEvent> = {
      'song-create': 'AddToCart',
      'plans-viewed': 'ViewContent',
      'checkout-started': 'InitiateCheckout',
      'signup': 'CompleteRegistration',
    };

    const standardEvent = map[collectionName];
    if (standardEvent) {
      this.metaPixelService.track(standardEvent, {
        content_category: data?.occasionId ?? data?.planId ?? undefined,
        // Wert nur wo einer existiert, und immer netto ohne MwSt. - dieselbe
        // Konvention wie serverseitig, sonst wird der ROAS unbrauchbar.
        ...(data?.priceGross
          ? {
              value: Math.round((Number(data.priceGross) / 1.19) * 100) / 100,
              currency: data.currency ?? 'EUR',
            }
          : {}),
      });
      return;
    }

    // Ereignisse ohne passenden Meta-Standardnamen.
    if (collectionName === 'form-started') {
      this.metaPixelService.trackCustom(collectionName, {
        content_category: data?.occasionId ?? undefined,
      });
      return;
    }

    // Nur gehoerte Previews sind ein Optimierungssignal. Ein Kaeufer, der seinen
    // fertigen Song nochmal anhoert, ist fuer Meta uninteressant - er hat schon
    // konvertiert. Und ein Play aus einem Share-Link kommt von einem
    // Beschenkten, nicht von einem potenziellen Kaeufer.
    if (collectionName === 'song-played') {
      if (data?.tier === 'preview' && !data?.shareView) {
        this.metaPixelService.trackCustom('preview-played', {
          content_category: data?.occasionId ?? undefined,
        });
      }
      return;
    }
  }

  /**
   * Funnel-Zwischenschritte. Bewusst generisch, damit neue Stufen keine neue
   * Methode brauchen.
   */
  async trackStep(step: AnalyticsEventName, data: any = {}): Promise<void> {
    await this.track(step, data);
  }

  /** Einmal pro Sitzung feuern - fuer Events, die ein Reload nicht wiederholen soll. */
  async trackOnce(
    dedupeKey: string,
    collectionName: AnalyticsEventName,
    data: any = {}
  ): Promise<void> {
    const key = `tracked:${collectionName}:${dedupeKey}`;
    if (this.hasTracked(key)) {
      return;
    }
    this.markTracked(key);
    await this.track(collectionName, data);
  }

  // =====================================================
  // CAMPAIGN
  // =====================================================

  /** Erste Zuordnung, sonst die der laufenden Sitzung. */
  private getStoredCampaign(): CampaignData | null {
    return (
      this.readJson(localStorage, this.firstAttributionKey) ??
      this.readJson(sessionStorage, this.currentAttributionKey)
    );
  }

  /** Fuer Order-Anreicherung und serverseitige Conversion-Events. */
  getAttributionSnapshot(): AttributionSnapshot {
    return {
      first: this.readJson(localStorage, this.firstAttributionKey),
      current: this.readJson(sessionStorage, this.currentAttributionKey),
    };
  }

  /**
   * Zuordnungsdaten fuer den Checkout. Werden auf die Order geschrieben, damit
   * das Purchase-Event serverseitig mit brauchbarer Match Quality an Meta gehen
   * kann - unabhaengig davon, ob der Nutzer den Tab schliesst oder einen
   * Adblocker nutzt.
   *
   * Bevorzugt die aktuelle Sitzung, faellt auf die Erst-Herkunft zurueck.
   */
  getCheckoutAttribution(): OrderAttribution {
    const snapshot = this.getAttributionSnapshot();
    const current: any = snapshot.current;
    const first: any = snapshot.first;
    const source: any = current ?? first ?? {};

    return {
      fbclid: source.fbclid ?? null,
      fbclidAt: current?.capturedAt ?? first?.firstSeenAt ?? null,
      // _fbp setzt der Meta-Browser-Pixel. Solange der nicht laeuft, ist das
      // null - das Event geht trotzdem, nur mit etwas schwaecherem Matching.
      fbp: this.readCookie('_fbp'),
      utm_source: source.utm_source ?? null,
      utm_medium: source.utm_medium ?? null,
      utm_campaign: source.utm_campaign ?? null,
      utm_content: source.utm_content ?? null,
      eventSourceUrl: window.location.href,
    };
  }

  private readCookie(name: string): string | null {
    try {
      const match = document.cookie.match(
        new RegExp('(^|;\\s*)' + name + '=([^;]*)')
      );
      return match ? decodeURIComponent(match[2]) : null;
    } catch {
      return null;
    }
  }

  private getCampaignFromUrl(): CampaignData {
    // Regulaerer Fall bei Path-Routing: die Parameter stehen im Querystring.
    const searchParams = new URLSearchParams(window.location.search);

    // Altlast: Parameter innerhalb des Fragments, z.B. aus Links, die vor der
    // Routing-Umstellung verschickt wurden.
    const hash = window.location.hash;
    const hashQuery = hash.includes('?')
      ? hash.slice(hash.indexOf('?') + 1)
      : '';
    const hashParams = new URLSearchParams(hashQuery);

    // search gewinnt, weil dort die Plattformen schreiben.
    const pick = (key: string): string | null => {
      const fromSearch = searchParams.get(key);
      if (fromSearch !== null && fromSearch !== '') return fromSearch;
      const fromHash = hashParams.get(key);
      return fromHash !== null && fromHash !== '' ? fromHash : null;
    };

    const campaign: Record<string, string | null> = {};
    for (const key of CAMPAIGN_PARAMS) {
      campaign[key] = pick(key);
    }
    return campaign as unknown as CampaignData;
  }

  private hasCampaign(campaign: CampaignData | null): boolean {
    if (!campaign) return false;
    return Object.values(campaign).some(Boolean);
  }

  // =====================================================
  // SESSION
  // =====================================================

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem(this.sessionKey);

    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(this.sessionKey, sessionId);
    }

    return sessionId;
  }

  private hasTracked(key: string): boolean {
    return sessionStorage.getItem(key) === '1';
  }

  private markTracked(key: string): void {
    sessionStorage.setItem(key, '1');
  }

  // =====================================================
  // HELPERS
  // =====================================================

  /**
   * Pfad ohne Querystring - stabil als Gruppierungs- und Dedupe-Schluessel.
   *
   * Bei Path-Routing steht die Route im pathname. Der Hash-Zweig deckt nur noch
   * Altlasten ab, falls der Shim in index.html nicht gegriffen hat. Ohne das
   * Abschneiden des Querystrings wuerde jede UTM-Kombination ein eigener Pfad
   * und der Dedupe-Schluessel waere wirkungslos.
   */
  private getCleanPath(): string {
    const hash = window.location.hash;
    const raw = hash.indexOf('#/') === 0 ? hash : window.location.pathname;
    return raw.split('?')[0];
  }

  private readJson(store: Storage, key: string): any | null {
    try {
      const raw = store.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private writeJson(store: Storage, key: string, value: unknown): void {
    try {
      store.setItem(key, JSON.stringify(value));
    } catch {
      // Privater Modus oder voller Storage: Tracking darf daran nicht scheitern.
    }
  }

  private extractHost(url: string | null): string | null {
    if (!url) return null;

    try {
      return new URL(url).hostname;
    } catch {
      return null;
    }
  }
}
